import { Platform, StyleSheet, TextInput, TextInputProps, View, ViewStyle } from 'react-native';
import { Divider } from 'react-native-paper';
import Animated, { Easing, FadeInLeft, runOnJS, useAnimatedStyle, withTiming } from 'react-native-reanimated';

import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

import PredictionButton from '../buttons/PredictionButton';
import DotsLoader from '../DotsLoader';
import Text from '../Text';

export interface PredictionType {
  value: string;
  label: string;
  extraLabel?: string;
  score?: {
    classic: number;
    semantic: number;
    combinedNormalized: number;
  };
  origin?: string;
}

interface TextInputAutocompleteProps extends TextInputProps {
  value: string;
  viewStyle?: ViewStyle | ViewStyle[];
  leadingIcon?: React.ReactNode;
  traillingIcon?: React.ReactNode;
  closeSuggestions?: boolean;
  onCloseSuggestions?: () => void;
  variant?: 'default' | 'rounded_with_shadow' | 'gray_borders';
  onChangeText: (text: string) => void;
  loading?: boolean;
  predictions: PredictionType[];
  showPredictions: boolean;
  predictionsScrollEnabled: boolean;
  showNoResult?: boolean;
  noResultComponent?: React.ReactElement;
  extraPredictionComponent?: React.ReactElement;
  extraPredictionItemHeight?: number;
  extraPredictionFullWidth?: boolean;
  onPredictionTapped: ({ value, label }: PredictionType) => void;
}

const TextInputAutocomplete = (props: TextInputAutocompleteProps) => {
  const colors = useColor();

  const {
    value,
    viewStyle,
    onChangeText,
    loading,
    onPredictionTapped,
    predictions,
    showPredictions,
    variant = 'default',
    leadingIcon,
    traillingIcon,
    closeSuggestions = false,
    onCloseSuggestions,
    showNoResult,
    noResultComponent,
    extraPredictionComponent,
    extraPredictionItemHeight,
    extraPredictionFullWidth,
    predictionsScrollEnabled
  } = props;

  //Styles
  const inputStyle = getInputStyle(variant);

  const passedStyles = Array.isArray(viewStyle) ? Object.assign({}, ...viewStyle) : viewStyle;

  //ViewableItems
  const numberOfItems = predictions?.length ?? 0;
  const effectiveItems = numberOfItems === 0 && showNoResult ? 1 : numberOfItems;
  const visibleItems = effectiveItems > 7 ? 7 : effectiveItems;

  const predictionsHasExtraLabel = predictions?.find((it) => it.extraLabel) !== undefined;

  //Constants
  const animationBaseDuration = 50;
  const defaultVariantHeight = 56;
  const enhancedVariantHeight = 38;
  const inputHeight = variant === 'default' ? defaultVariantHeight : enhancedVariantHeight;
  const predictionHeight = predictionsHasExtraLabel ? 56 : 38;

  //Animation
  const totalAnimationDuration = Math.max(300, visibleItems * animationBaseDuration);

  const hasExtraComponent = extraPredictionComponent !== undefined;
  const extraComponentHeight = hasExtraComponent ? (extraPredictionItemHeight ?? predictionHeight) : 0;

  const animatedSearchStyle = useAnimatedStyle(() => {
    // Basic Heights
    const separatorHeight = 1;
    const separators = visibleItems > 0 ? visibleItems - 1 : 0;
    const listHeight = predictionHeight * visibleItems + separators * separatorHeight;
    const extraLabelAdjustment = visibleItems > 0 && predictionsHasExtraLabel ? 18 : 0;

    const targetHeight = !showPredictions
      ? inputHeight
      : inputHeight + listHeight + extraLabelAdjustment + extraComponentHeight;

    return {
      height: withTiming(
        targetHeight,
        {
          duration: totalAnimationDuration,
          easing: Easing.linear
        },
        () => {
          if (closeSuggestions && onCloseSuggestions) {
            runOnJS(onCloseSuggestions)();
          }
        }
      )
    };
  });

  //Locales
  const noResultText = i18n.t('mix.no_result');

  return (
    <View
      style={[
        styles.textInputContainer,
        {
          height: variant === 'default' || variant === 'gray_borders' ? defaultVariantHeight : enhancedVariantHeight
        }
      ]}>
      <Animated.View
        style={[animatedSearchStyle, variant === 'default' ? styles.container : inputStyle, { ...passedStyles }]}>
        <View
          style={[
            styles.inputContainer,
            {
              height:
                variant === 'rounded_with_shadow' || variant === 'gray_borders'
                  ? enhancedVariantHeight
                  : defaultVariantHeight
            }
          ]}>
          {leadingIcon}
          <TextInput
            {...props}
            placeholderTextColor={colors.placeholderText}
            style={[styles.flexOne, Platform.OS === 'web' && ({ outlineStyle: 'none', fontSize: 16 } as any)]}
            value={value}
            onChangeText={onChangeText}
            returnKeyType="search"
            allowFontScaling={false}
          />
          {loading && value.length > 0 && <DotsLoader size={4} />}
          {traillingIcon}
        </View>
        {showPredictions && (
          <Animated.FlatList
            showsVerticalScrollIndicator={false}
            scrollEnabled={predictionsScrollEnabled}
            nestedScrollEnabled={Platform.OS === 'android'}
            data={predictions}
            bounces={false}
            renderItem={({ item, index }: { item: PredictionType; index: number }) => {
              if (index < visibleItems)
                return (
                  <Animated.View
                    entering={FadeInLeft.delay(index * animationBaseDuration)}
                    style={styles.horizontalPadding}>
                    <PredictionButton item={item} onPredictionTapped={onPredictionTapped} />
                  </Animated.View>
                );

              return (
                <View style={styles.horizontalPadding}>
                  <PredictionButton item={item} onPredictionTapped={onPredictionTapped} />
                </View>
              );
            }}
            {...(showNoResult && {
              ListEmptyComponent:
                noResultComponent !== undefined ? (
                  () => noResultComponent
                ) : (
                  <View style={[styles.predictionRow, styles.horizontalPadding]}>
                    <Text numberOfLines={1}>{noResultText}</Text>
                  </View>
                )
            })}
            ListFooterComponent={
              extraPredictionComponent !== undefined
                ? extraPredictionFullWidth
                  ? () => extraPredictionComponent
                  : () => <View style={styles.horizontalPadding}>{extraPredictionComponent}</View>
                : null
            }
            ItemSeparatorComponent={() => <Divider />}
            keyExtractor={(_: unknown, index: number) => index.toString()}
            keyboardShouldPersistTaps="handled"
          />
        )}
      </Animated.View>
    </View>
  );
};

const getInputStyle = (variant: string) => {
  switch (variant) {
    case 'rounded_with_shadow':
      return styles.roundedWithShadow;

    case 'gray_borders':
      return styles.grayBorders;

    default:
      return styles.inputStyle;
  }
};

const styles = StyleSheet.create({
  textInputContainer: {
    zIndex: 1000
  },

  container: {
    justifyContent: 'flex-start',
    borderWidth: 1,
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: 'white',
    overflow: 'hidden'
  },

  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 'normal',
    paddingHorizontal: 16
  },

  roundedWithShadow: {
    backgroundColor: 'white',
    borderRadius: 12,
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowRadius: 5,
    shadowColor: 'black',
    shadowOpacity: 0.12,
    elevation: 5,
    zIndex: 1000
  },

  grayBorders: {
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 8,
    borderColor: 'darkgray',
    backgroundColor: 'white'
  },

  inputStyle: {
    paddingVertical: 16,
    borderWidth: 1,
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: 'white'
  },

  predictionRow: {
    paddingVertical: 10
  },

  flexOne: {
    flex: 1
  },

  horizontalPadding: {
    paddingHorizontal: 16
  }
});

export default TextInputAutocomplete;
